import React, { useState, useEffect } from 'react';
import { useClientAuth } from '../contexts/ClientAuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { LogIn, UserPlus, AlertCircle, Smartphone, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const ClientLogin = () => {
  const [isSignup, setIsSignup] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, signup } = useClientAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // PWA Install states - compute initial canInstall value
  const isStandaloneMode = typeof window !== 'undefined' && (
    window.matchMedia('(display-mode: standalone)').matches || 
    window.navigator.standalone === true
  );
  const [installPrompt, setInstallPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(!isStandaloneMode);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  
  // Compute iOS detection once
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setCanInstall(true);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallApp = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }
    
    if (!installPrompt) {
      toast({
        title: "Install App",
        description: 'Use your browser menu and select "Add to Home Screen"',
      });
      return;
    }
    
    try {
      await installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      
      if (outcome === 'accepted') {
        toast({
          title: "App Installed",
          description: "Find it on your home screen!",
        });
        setCanInstall(false);
      }
      setInstallPrompt(null);
    } catch (error) {
      console.error('Install error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    let result;
    if (isSignup) {
      result = await signup(name, email, phone, password);
    } else {
      result = await login(email, password);
    }
    
    if (result.success) {
      toast({
        title: isSignup ? "Account Created!" : "Welcome Back!",
        description: isSignup ? "Your account has been created successfully" : "Logged in successfully",
      });
      navigate('/client-portal');
    } else {
      setError(result.error);
      toast({
        title: "Error",
        description: result.error,
        variant: "destructive"
      });
    }
    
    setLoading(false);
  };

  return (
    <div className="client-login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1 className="heading-1">VISAASURE</h1>
            <p className="body-medium">{isSignup ? 'Create Your Account' : 'Client Portal'}</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            {error && (
              <div className="error-message">
                <AlertCircle size={20} />
                <span>{error}</span>
              </div>
            )}

            {isSignup && (
              <div className="form-group">
                <label htmlFor="name" className="form-label body-small">Full Name</label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="form-input"
                  placeholder="Enter your full name"
                  disabled={loading}
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email" className="form-label body-small">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="form-input"
                placeholder="your.email@example.com"
                disabled={loading}
              />
            </div>

            {isSignup && (
              <div className="form-group">
                <label htmlFor="phone" className="form-label body-small">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  className="form-input"
                  placeholder="+91 XXXXX XXXXX"
                  disabled={loading}
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="password" className="form-label body-small">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="form-input"
                placeholder="Enter your password"
                disabled={loading}
              />
            </div>

            <button 
              type="submit" 
              className="btn-primary login-btn"
              disabled={loading}
            >
              {loading ? (
                <span>{isSignup ? 'Creating Account...' : 'Logging in...'}</span>
              ) : (
                <>
                  {isSignup ? <UserPlus size={20} /> : <LogIn size={20} />}
                  <span>{isSignup ? 'Create Account' : 'Login to Portal'}</span>
                </>
              )}
            </button>
          </form>

          <div className="login-footer">
            <button 
              onClick={() => setIsSignup(!isSignup)}
              className="toggle-auth-btn"
            >
              {isSignup ? 'Already have an account? Login' : "Don't have an account? Sign up"}
            </button>
            
            {/* Install App Button */}
            {canInstall && (
              <button
                type="button"
                onClick={handleInstallApp}
                className="toggle-auth-btn"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '8px', color: '#3b82f6' }}
              >
                <Smartphone size={16} />
                <span>Install App</span>
              </button>
            )}
            
            <Link to="/" className="back-home-link caption">
              Back to Home
            </Link>
          </div>
        </div>
      </div>

      {/* iOS Instructions Modal */}
      {showIOSInstructions && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            maxWidth: '400px',
            width: '100%',
            padding: '24px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#111827' }}>Install on iPhone/iPad</h3>
              <button 
                onClick={() => setShowIOSInstructions(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}
              >
                <X size={24} />
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', fontSize: '14px', color: '#4b5563' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ width: '32px', height: '32px', background: '#dbeafe', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontWeight: 'bold', color: '#2563eb' }}>1</span>
                </div>
                <p>Tap the <strong>Share</strong> button at the bottom of Safari</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ width: '32px', height: '32px', background: '#dbeafe', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontWeight: 'bold', color: '#2563eb' }}>2</span>
                </div>
                <p>Scroll down and tap <strong>&quot;Add to Home Screen&quot;</strong></p>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ width: '32px', height: '32px', background: '#dbeafe', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontWeight: 'bold', color: '#2563eb' }}>3</span>
                </div>
                <p>Tap <strong>&quot;Add&quot;</strong> in the top right corner</p>
              </div>
            </div>
            <button 
              onClick={() => setShowIOSInstructions(false)}
              className="btn-primary"
              style={{ width: '100%', marginTop: '24px' }}
            >
              Got it!
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientLogin;